// packages/core/src/learning/schools/technical-trading.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no figures; canon is
// conceptual.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const TECHNICAL_TRADING_EXHIBIT: SchoolExhibitEntry = {
	id: 'technical-trading',
	tagline:
		'Technical trading believes price behavior can reveal crowd pressure.',
	worldviewSummary:
		'Technical trading is paying attention to price movement, trend, volume, setups, risk control, and market behavior under pressure. This lens protects you from thinking only fundamentals matter when real buyers and sellers are moving the price right now. It accepts a serious tradeoff: charts can create false confidence, patterns can fail, losses are possible, and outcomes are not reliable. The blind spot is signal worship. A chart can look clean while the trade is still speculation, and speculation is not dependable wealth.',
	coreBeliefs: [
		'Technical trading believes price behavior contains useful information about supply, demand, pressure, and attention.',
		'Technical trading believes timing matters because the same idea can behave differently depending on entry, exit, and market condition.',
		'Technical trading believes risk control matters more than being right on every setup.',
		'Technical trading believes trends can persist, but they can also reverse without warning.',
		'Technical trading believes setups are only useful when paired with position sizing, exits, and emotional discipline.',
		'Technical trading believes losses are part of the environment, not proof that the next setup must work.',
		'Technical trading believes charts can guide decisions, but they cannot make speculation reliable.',
	],
	warnsAgainst: [
		'Treating a chart pattern like a prediction.',
		'Ignoring position size, exit rules, liquidity, and emotional pressure.',
		'Confusing a lucky streak with skill.',
		'Believing technical trading creates reliable wealth or removes the risk of loss.',
	],
	helps: 'This lens helps people understand how traders think about price behavior, timing, crowd psychology, and risk control. It may not fit someone who needs simplicity, low stress, long horizon investing, or a money system that avoids frequent decisions.',
	figures: [],
	canon: [
		{
			title: 'Trend following',
			oneLineIdea:
				'The lens studies whether price strength or weakness is continuing, while accepting that reversals can be painful.',
		},
		{
			title: 'Support and resistance',
			oneLineIdea:
				'Traders watch areas where buyers or sellers may have reacted before, but those areas can fail.',
		},
		{
			title: 'Breakout trading',
			oneLineIdea:
				'A breakout tries to read a shift in pressure, but false breakouts can trap late entries.',
		},
		{
			title: 'Risk per trade',
			oneLineIdea:
				'A setup matters less if one bad decision can damage the whole account.',
		},
		{
			title: 'Trading journal',
			oneLineIdea:
				'The point is to separate process from emotion, luck, and selective memory.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-technical-trading',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your runway can handle speculative losses',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
		{
			label: 'Compare trading risk against your real money picture',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test how speculation changes your plan',
			action: {
				type: 'openSimulator',
				label: 'Simulate a risk case',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the exit rule before the setup looks exciting',
			action: {
				type: 'noToolAction',
				label: 'Write the exit rule',
				prompt: 'Write what would make you exit a trade before writing why you would enter it.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'A chart is not a prophecy. It is a map of pressure and behavior.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of trading is how fast emotion can become strategy.',
			kind: 'hook',
		},
		{
			line: 'What do beginners misunderstand when they think a pattern means a prediction?',
			kind: 'interview-question',
		},
		{
			line: 'When did you confuse a lucky streak with skill?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches technical trading as speculation with risk controls, not as reliable wealth.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Users should understand timing, exits, and loss paths before they trust a setup.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why a clean chart can still fail.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone to explain their exit before they explain their entry.',
			kind: 'creator-prompt',
		},
		{
			line: 'Walk through how one oversized trade can erase ten good decisions.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
